import React, { useCallback, useEffect, useState } from 'react';
import { clsx } from 'clsx';
import { Loader2, ShieldCheck, Target, UserCheck, UserX, Users } from 'lucide-react';
import { Button } from '@components/Button';
import { ApiErrorModal } from '@components/ApiErrorModal';
import { Switch } from '@components/Switch';
import { useProfile } from '@contexts/ProfileContext';
import profileService from '@services/profileService';
import type { AppUser } from '@/types/planning';
import { formatDateTimeBR } from '@/utils/formatters';

/**
 * Administração → Usuários: quem já entrou na aplicação (AppUser), com ativar/desativar.
 * O admin NÃO vê dados de outros usuários — só o cadastro. Papéis vêm do Entra ID.
 */
export const UsersAdminView: React.FC = () => {
  const { profile } = useProfile();
  const [users, setUsers] = useState<AppUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [cacBusyId, setCacBusyId] = useState<number | null>(null);
  const [error, setError] = useState<unknown>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setUsers(await profileService.listUsers());
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = async (u: AppUser) => {
    setBusyId(u.user_id);
    try {
      const updated = await profileService.setUserActive(u.user_id, !u.is_active);
      setUsers((prev) => prev.map((x) => (x.user_id === updated.user_id ? { ...x, ...updated } : x)));
    } catch (err) {
      setError(err);
    } finally {
      setBusyId(null);
    }
  };

  const toggleCac = async (u: AppUser) => {
    setCacBusyId(u.user_id);
    try {
      const updated = await profileService.setUserCac(u.user_id, !u.cac_enabled);
      setUsers((prev) => prev.map((x) => (x.user_id === updated.user_id ? { ...x, ...updated } : x)));
    } catch (err) {
      setError(err);
    } finally {
      setCacBusyId(null);
    }
  };

  /** Interruptor individual do Acervo CAC + badge do efeito (admin: sempre liberado, sem interruptor). */
  const cacControl = (u: AppUser) => {
    if (u.role === 'admin') {
      return (
        <span className="badge-success inline-flex items-center gap-1" title="O administrador sempre tem o Acervo CAC">
          <Target size={11} /> sempre liberado
        </span>
      );
    }
    const badge = u.cac_effective ? (
      <span className="badge-success">{u.cac_enabled ? 'liberado' : 'liberado (geral)'}</span>
    ) : (
      <span className="badge-neutral">não liberado</span>
    );
    return (
      <div className="flex flex-wrap items-center gap-2">
        <Switch
          checked={!!u.cac_enabled}
          onChange={() => toggleCac(u)}
          loading={cacBusyId === u.user_id}
          disabled={cacBusyId !== null || !u.is_active}
          ariaLabel={`Liberar Acervo CAC para ${u.name}`}
        />
        {badge}
      </div>
    );
  };

  const roleBadge = (u: AppUser) =>
    u.role === 'admin' ? (
      <span className="badge-info inline-flex items-center gap-1">
        <ShieldCheck size={11} /> Administrador
      </span>
    ) : (
      <span className="badge-neutral">Usuário</span>
    );

  const toggleButton = (u: AppUser) => (
    <Button
      variant="outline"
      size="sm"
      icon={u.is_active ? <UserX /> : <UserCheck />}
      onClick={() => toggle(u)}
      loading={busyId === u.user_id}
      disabled={busyId !== null || u.user_id === profile?.user_id}
      className={clsx(u.is_active ? 'text-red-600 border-red-200 hover:bg-red-50' : 'text-emerald-700 border-emerald-200 hover:bg-emerald-50')}
      title={u.user_id === profile?.user_id ? 'Você não pode desativar o próprio acesso' : undefined}
    >
      {u.is_active ? 'Desativar' : 'Ativar'}
    </Button>
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-h3 flex items-center gap-2">
          <Users size={20} className="text-slate-500" /> Usuários
        </h1>
      </div>
      <p className="text-small">
        Lista de quem já entrou no Plan. O acesso é concedido no Entra ID (atribuição do aplicativo); aqui é possível
        desativar um acesso e liberar o <strong>Acervo CAC</strong> por usuário (a liberação para todos fica em{' '}
        <a href="/admin/settings" className="text-emerald-700 hover:underline">
          Configurações
        </a>
        ). Cada usuário vê apenas os próprios dados — inclusive o administrador.
      </p>

      {/* Celular: cartões */}
      <div className="md:hidden space-y-2">
        {loading && users.length === 0 && (
          <p className="text-center text-slate-400 py-6">
            <Loader2 size={18} className="inline animate-spin mr-2" /> Carregando…
          </p>
        )}
        {users.map((u) => (
          <div key={u.user_id} className={clsx('card p-3 space-y-2', !u.is_active && 'opacity-60')}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="font-medium text-slate-800">{u.name}</p>
                <p className="text-small [overflow-wrap:anywhere]">{u.email}</p>
              </div>
              {roleBadge(u)}
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2 text-small">
              <span>{u.last_login_at ? `Último acesso ${formatDateTimeBR(u.last_login_at)}` : 'Nunca acessou'}</span>
              {u.is_active ? <span className="badge-success">Ativo</span> : <span className="badge-neutral">Inativo</span>}
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2 text-small">
              <span className="inline-flex items-center gap-1">
                <Target size={12} /> Acervo CAC
              </span>
              {cacControl(u)}
            </div>
            {toggleButton(u)}
          </div>
        ))}
      </div>

      {/* Tabela */}
      <div className="table-container bg-white hidden md:block">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="table-header">
              <tr>
                <th>Usuário</th>
                <th>Papel</th>
                <th>Último acesso</th>
                <th>Situação</th>
                <th>Acervo CAC</th>
                <th className="w-32"></th>
              </tr>
            </thead>
            <tbody>
              {loading && users.length === 0 && (
                <tr>
                  <td colSpan={6} className="table-cell text-center text-slate-400">
                    <Loader2 size={18} className="inline animate-spin mr-2" /> Carregando…
                  </td>
                </tr>
              )}
              {!loading && users.length === 0 && (
                <tr>
                  <td colSpan={6} className="table-cell text-center text-slate-500 py-8">
                    Nenhum usuário entrou ainda.
                  </td>
                </tr>
              )}
              {users.map((u) => (
                <tr key={u.user_id} className={clsx('table-row', !u.is_active && 'opacity-60')}>
                  <td className="table-cell">
                    <p className="font-medium text-slate-800">{u.name}</p>
                    <p className="text-small">{u.email}</p>
                  </td>
                  <td className="table-cell">{roleBadge(u)}</td>
                  <td className="table-cell whitespace-nowrap">
                    {u.last_login_at ? formatDateTimeBR(u.last_login_at) : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="table-cell">
                    {u.is_active ? <span className="badge-success">Ativo</span> : <span className="badge-neutral">Inativo</span>}
                  </td>
                  <td className="table-cell">{cacControl(u)}</td>
                  <td className="table-cell text-right">{toggleButton(u)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <ApiErrorModal error={error} onClose={() => setError(null)} />
    </div>
  );
};
